<template>
  <div
    :class="{
      'mobile-sidebar': true,
      'catalog-sidebar': true,
      'catalog-filters-sidebar': true,
      row: true,
      open: isSidebarOpen,
    }"
    data-sidebar="catalog-filters"
  >
    <button
      class="btn btn-link close-sidebar-button d-md-none"
      @click="closeSidebar"
    >
      <IconClose />
    </button>
    <button
      v-if="showBackButton"
      class="btn btn-link page-back-button d-md-none"
      @click="sidebarPageBack"
    >
      <IconChevronLeft />
    </button>
    <div class="container">
      <div class="row">
        <div class="col sidebar-main-page">
          <h3 class="page-heading">
            {{ gettext('Filters') }}
          </h3>
          <div class="page-content">
            <CatalogFilter
              v-for="filter in visibleFilters"
              ref="filters"
              :key="filter.title"
              :filter="filter"
              :class="filter.class"
              @openSidebarPage="showBackButton = true"
              @filter-update="onFilterUpdate(filter, $event)"
            />
          </div>
        </div>
      </div>
    </div>
    <button
      v-if="showResultsButton"
      class="btn btn-primary button-show-products w-100 d-flex justify-content-center align-items-center d-md-none"
      @click="closeSidebar"
    >
      <span class="flex-grow-1">{{ gettext('Bekijk producten') }}</span>
      <IconChevronRight class="white" />
    </button>
  </div>
</template>

<script>
import CatalogFilter from './CatalogFilter.vue';
import IconClose from '../../../../templates/icons/close.svg';
import IconChevronLeft from '../../../../templates/icons/chevron-left.svg';
import IconChevronRight from '../../../../templates/icons/chevron-right.svg';
import { sortOnPreferedOrder } from '../../util/sorting';

const preferedSizeOrder = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];

export default {
  components: {
    CatalogFilter,
    IconClose,
    IconChevronLeft,
    IconChevronRight,
  },
  props: {
    open: Boolean,
    hideStoreFilter: Boolean,
    hideBrandFilter: Boolean,
  },
  data() {
    return {
      isSidebarOpen: this.open,
      showResultsButton: false,
      showBackButton: false,
      filters: [
        {
          title: this.gettext('Sorteren'),
          type: 'sort',
          class: 'd-md-none',
        },
        {
          title: this.gettext('Fair Fashion Giftcard'),
          hide: this.$store.state.countryCode !== 'NL',
          info: this.gettext(
            'Filter on products from webshops that accept the Fair Fashion Giftcard'
          ),
          attribute: 'fair_fashion_giftcard',
          type: 'toggle',
          label: this.gettext('Te koop met Fair Fashion Giftcard'),
          link: {
            text: this.gettext('Meer weten? Klik hier!'),
            url: '/fairfashiongiftcard/',
          },
          eventKey: 'Giftcard',
        },
        {
          title: this.gettext('Sale'),
          attribute: 'discounted',
          type: 'toggle',
          label: this.gettext('Only show SALE'),
          eventKey: 'Sale',
        },
        {
          title: this.gettext('Winkel locatie'),
          attribute: 'store_location',
          info: this.gettext(
            'Filter on products which are shipped from the selected countries'
          ),
          type: 'list',
          searchable: true,
          searchablePlaceholder: this.gettext('Zoek op winkel locatie...'),
          limit: true,
          showMoreButton: this.gettext('Meer winkel locaties...'),
          showLessButton: this.gettext('Minder winkel locaties...'),
          getLabel: value => this.$store.getters.getCountryName(value),
          eventKey: 'Country',
        },
        {
          title: this.gettext('Label'),
          attribute: 'label_ids',
          operator: 'and',
          info: this.gettext(
            'Filter on products with all the selected sustainability labels. These indicate different aspects of sustainability.'
          ),
          type: 'list',
          getLabel: value => this.$store.state.labels[value],
          getIcon: value => `/static/img/labels/${value}.png`,
          eventKey: 'Label',
        },
        {
          title: this.gettext('Prijs'),
          attribute: `price_${this.$store.state.currency.toLowerCase()}`,
          type: 'price',
          eventKey: 'Price',
        },
        {
          title: this.gettext('Maat'),
          attribute: 'size_labels',
          type: 'list',
          searchable: true,
          searchablePlaceholder: this.gettext('Zoek op maat...'),
          limit: true,
          showMoreButton: this.gettext('Meer maten...'),
          showLessButton: this.gettext('Minder maten...'),
          transformItems: items =>
            sortOnPreferedOrder(preferedSizeOrder, items),
          eventKey: 'Size',
        },
        {
          title: this.gettext('Merk'),
          hide: this.hideBrandFilter,
          info: this.gettext(
            'Filter on brand. The brand is responsible for designing and producing the items. This can be different from the store that sells the product as a store can sell items from multiple brands.'
          ),
          attribute: 'brand_name',
          type: 'list',
          searchable: true,
          searchablePlaceholder: this.gettext('Zoek op merk...'),
          limit: true,
          showMoreButton: this.gettext('Meer merken...'),
          showLessButton: this.gettext('Minder merken...'),
          eventKey: 'Brand',
        },
        {
          title: this.gettext('Kleur'),
          attribute: 'color',
          type: 'list',
          getLabel: value => this.$store.getters.getColor(value),
          limit: true,
          showMoreButton: this.gettext('Meer kleuren...'),
          showLessButton: this.gettext('Minder kleuren...'),
          eventKey: 'Color',
        },
        {
          title: this.gettext('Winkel'),
          hide: this.hideStoreFilter,
          attribute: 'store_name',
          info: this.gettext(
            'Filter on online store. The store is responsible for selling and sending the product to you. Some stores sell multiple brands.'
          ),
          type: 'list',
          searchable: true,
          searchablePlaceholder: this.gettext('Zoek op winkel...'),
          limit: true,
          showMoreButton: this.gettext('Meer winkels...'),
          showLessButton: this.gettext('Minder winkels...'),
          eventKey: 'Store',
        },
        {
          title: this.gettext('Keurmerk'),
          attribute: 'certificate_names',
          info: this.gettext(
            'Filter on products that have all the selected certificates. Read more about what each certificate means on our certificate pages!'
          ),
          type: 'list',
          limit: true,
          showMoreButton: this.gettext('Meer keurmerken...'),
          showLessButton: this.gettext('Minder keurmerken...'),
          eventKey: 'Certificate',
        },
        {
          title: this.gettext('Materiaal'),
          attribute: 'material_ids',
          type: 'list',
          getLabel: value => this.$store.getters.getMaterialName(value),
          limit: true,
          showMoreButton: this.gettext('Meer materialen...'),
          showLessButton: this.gettext('Minder materialen...'),
          eventKey: 'Material',
        },
      ],
    };
  },
  computed: {
    visibleFilters() {
      return this.filters.filter(f => !f.hide);
    },
  },
  watch: {
    open(val) {
      if (val !== this.isSidebarOpen) {
        this.isSidebarOpen = val;
        if (this.isSidebarOpen) {
          document.body.classList.add('no-scroll');
        } else {
          document.body.classList.remove('no-scroll');
        }
      }
    },
  },
  methods: {
    onFilterUpdate(filter, value) {
      this.showResultsButton = true;
      if (value) {
        window.tracking.trackEvent(
          'FilterSelect',
          `${filter.eventKey} Filter`,
          `${filter.eventKey}: ${value}`
        );
      }
    },
    sidebarPageBack() {
      this.closeAllSidebarPages();
      this.showBackButton = false;
    },
    closeSidebar() {
      this.$emit('onClose');
      this.showResultsButton = false;
      this.showBackButton = false;
      this.closeAllSidebarPages();
    },
    closeAllSidebarPages() {
      this.$refs.filters.forEach(filter => {
        filter.closeSidebarPage();
      });
    },
  },
};
</script>
